import { useQueryClient } from '@tanstack/react-query';
import { DateTime, Duration } from 'luxon';
import { FC, MouseEvent, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import GoToLink from '@/components/GoToLink';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { EYTVLinkResource } from '@/types/resources';
import { api } from '@/utils/api';
import { EYTVLinkStatus } from '@/utils/enums';

import LinkStatus from './LinkStatus';
import { Checkbox, CheckboxHolder, CheckboxLabel } from './VideosListing';

const Tr = styled.tr<{ $isExported: boolean }>`
  ${({ theme, $isExported }) =>
    $isExported &&
    css`
      > ${Td} {
        color: ${theme.colors.gray};
      }
    `}
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  link: EYTVLinkResource;
  checked: boolean;
  onCheck: VoidFunction;
}

const LinksTableRow: FC<Props> = ({ link, checked, onCheck }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this video?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/eytv-links/${link.id}`);

      await queryClient.invalidateQueries(['eytv_links']);

      alerts.success('Video was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const trimTime = useMemo(() => {
    const from = Duration.fromObject({ seconds: link.startTime }).toFormat(
      'hh:mm:ss',
    );
    const until = Duration.fromObject({ seconds: link.endTime }).toFormat(
      'hh:mm:ss',
    );

    return `${from} - ${until}`;
  }, [link]);

  return (
    <Tr $isExported={link.isExported}>
      <Td>
        <CheckboxHolder>
          <Checkbox
            disabled={link.status !== EYTVLinkStatus.DONE}
            id={`link-${link.id}`}
            type="checkbox"
            checked={checked}
            onChange={onCheck}
          />
          <CheckboxLabel htmlFor={`link-${link.id}`} />
        </CheckboxHolder>
      </Td>
      <Td>{link.title}</Td>
      <Td>
        <LinkStatus status={link.status} />
      </Td>
      <Td>{trimTime}</Td>
      <Td>
        {link.youtubeVideoId ? (
          <Links>
            <div>
              <GoToLink
                link={`https://youtube.com/watch?v=${link.youtubeVideoId}`}
                title="Youtube Video"
              />
            </div>
            <div>
              <GoToLink
                link={`https://youtube.com/${link.youtubeChannelId}`}
                title="Youtube Channel"
              />
            </div>
          </Links>
        ) : (
          '-'
        )}
      </Td>
      <Td>{DateTime.fromISO(link.createdAt).toLocaleString()}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default LinksTableRow;
